import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Cell from './Cell';
import { IonActionSheet } from '@ionic/react';
import { albumsOutline, arrowBack, backspace, checkmark, clipboardOutline, closeCircle, closeOutline, copyOutline, createOutline, grid, trashOutline } from 'ionicons/icons';
import { _NEW_LINE, _SPACE_CARD, _defaultCardSize, _defaultParams, _dict } from '../constants';
import { useGlobalStore } from '../stateManagement/globalZustand';
import { useTreeStore } from '../stateManagement/treeZustand';
import { useURLStore } from '../stateManagement/URLZustand';
import { useUIStore } from '../stateManagement/UIZustand';
import ButtonTemplate from '../templates/ButtonTemplate';
import Card from '../Card';
import ImageRetrievalModal from '../ImageRetrievalModal';
import { GridCellProps, convertLabelsToGridCellList, defaultCellProps, gatherLabels, getNewLineIndices, getPropsWhenConfirmed, makeSelectedCellsEmpty, reRandomizeAliasLabel, reindexCells, removeSelectedCellsEntireRows, transformArrayTo2D } from '../../foo/grid';
import { useHistory } from 'react-router';
import CopyDialog from '../dialogs/CopyDialog';


interface GridProps {
  totalCells: number;
}

const GridMainComponent: React.FC<GridProps> = ({ totalCells }) => {
  const [cellStates, setCellStates] = useState<GridCellProps[][]>();
  const [copiedCells, setCopiedCells] = useState<{ [key: number]: GridCellProps }>({});
  const [initialized, setInitialized] = useState<boolean>(false);
  const [showPasteOptions, setShowPasteOptions] = useState<boolean>(false);
  const [showRemoveOptions, setShowRemoveOptions] = useState<boolean>(false);
  const { IRLabel, setIRLabel, isIROpen, setIsIROpen, setIRLabelIndex } = useUIStore();
  const [whatShouldICopy, setWhatShouldICopy] = useState<{ [key: string]: any }>({});



  const [numOfCols, setNumOfCols] = useState<number>(3);

  const { nodes, setNodes, l2Image, setL2Image, l2Color, setL2Color, path2Params, getImage, setPath2Params, defaultParams } = useTreeStore();
  const { isGridMode, setIsGridMode } = useUIStore();
  const { URLVars, getURLTo, getParentNode } = useURLStore();
  const { lang, isParentMode } = useGlobalStore();
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState<boolean>(false);
  const [divWidth, setDivWidth] = useState(0);
  const [doUpdateGlobalStatesToggle, setDoUpdateGlobalStatesToggle] = useState<boolean>(false);
  const mainDivRef = useRef(null);

  const history = useHistory();

  const cardWidth = useMemo(() =>
    path2Params[URLVars.nodePath!]?.cardWidth ?? _defaultParams.cardWidth,
    [path2Params, URLVars.nodePath]
  );


  const nrCols = useMemo(() =>
    path2Params[URLVars.nodePath!]?.nrCols ?? _defaultParams.nrCols,
    [path2Params, URLVars.nodePath]
  );


  useEffect(() => {
    const divElement = mainDivRef.current;
    if (!divElement) return; // Early exit if ref is null

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]; // Assuming there's only one element being observed
      if (entry) {
        setDivWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(divElement);

    // Cleanup function to disconnect the ResizeObserver
    return () => {
      resizeObserver.disconnect();
    };
  }, [mainDivRef.current]); // React guarantees refs are stable, so you can use .current in dependencies



  useEffect(() => {

    const oldNrCols = nrCols

    if (oldNrCols === null || oldNrCols === 0) {
      const numOfCols = Math.floor(divWidth / cardWidth);
      setNumOfCols(numOfCols);
      setPath2Params({ ...path2Params, [URLVars.nodePath!]: { ...path2Params[URLVars.nodePath!], nrCols: numOfCols } });
    } else {
      setNumOfCols(oldNrCols);
    }
  }, [divWidth, cardWidth]);


  useEffect(() => {
    if (URLVars.nodePath! !== undefined) {

      let labels = [...nodes[URLVars.nodePath!] ?? []];

      let newLineIndices = getNewLineIndices(labels);

      // Create a list of GridCellProps from the labels taken from nodes[URLVars.nodePath!]
      //const labelsWithNoNewLines = nodes[URLVars.nodePath!].filter((label: string) => label !== _NEW_LINE);
      const initialCells1D = convertLabelsToGridCellList(labels);
      // Update newLineIndices to reflect the new breaking points after removing _NEW_LINE labels
      newLineIndices = newLineIndices.map((index, n) => index - n + 1);
      const initialCells2D = transformArrayTo2D(initialCells1D, numOfCols, newLineIndices, { ...defaultCellProps }, true, totalCells);
      const initialCells2DReindexed = reindexCells(initialCells2D, numOfCols);

      // Set images and colors for the cells
      initialCells2DReindexed.forEach(row => {
        row.forEach(cell => {
          if (cell.label !== '') {
            const img = getImage(cell.label) ?? '';
            const color = l2Color[cell.label] ?? '';
            cell.image = img;
            cell.color = color;
          }
        });
      });
      setCellStates(initialCells2DReindexed);
      setInitialized(true);
    }
  }, [numOfCols]);



  // Gather selected indices from cellStates and useMemo to prevent unnecessary re-renders
  const selectedIndices = useMemo(() => {
    return cellStates?.flat().reduce((acc: number[], cell, index) => {
      if (cell.isSelected) {
        acc.push(index);
      }
      return acc;
    }, []);
  }, [cellStates]) || [];


  // USE-EFFECT-1: Update nodes when cellStates changes but are initialized
  useEffect(() => {
    if (cellStates === undefined) return;
    if (!initialized) return;
    const newBranchNodes = gatherLabels(cellStates!)
    // Iterate over all cellStates and use color property to update l2Color
    const newL2Color = cellStates.reduce((acc: { [key: string]: string }, row) => {
      row.forEach(cell => {
        if (cell.label !== '') {
          acc[cell.label] = cell.color;
        }
      });
      return acc;
    }, {});

    setNodes({ ...nodes, [URLVars.nodePath!]: newBranchNodes });
    setDoUpdateGlobalStatesToggle(prev => !prev);
  }, [cellStates, initialized]);


  // Why to use doUpdateGlobalStatesToggle?
  /*
  In the backend it is not possible to change color, image, etc. if the label does not
  exist in nodes (this is done to avoid garbage collection!).
  It means, those properties can be updated only AFTER nodes are updated.

  The whole chain is:
  - Initialize cellStates from global states
  - Once initialized, when cellStates changes it updates nodes (i.e. labels only! See USE-EFFECT-1)
  - Once nodes are updated, the signal is toggled and the following UseEffect
  is triggered to update all properties.

  What happens if instead you DIRECTLY update globally from the local cell props?
  -> When there is a copy-paste process, it changes the local props, but also the label because of the
  re-randomized alias (reason for this, is to make labels unique despite copy-pasting).
  -> Therefore if we immediately update the global states, the new label is not yet in nodes, and the
  update is ignored!
   */
  useEffect(() => {
    if (!initialized) return;
    // Iterate over all cellStates and update l2Color based on the cellStates
    const newL2Color = cellStates?.reduce((acc: { [key: string]: string }, row) => {
      row.forEach(cell => {
        if (cell.label !== '') {
          acc[cell.label] = cell.color;
        }
      });
      return acc;
    }, {});
    setL2Color({ ...l2Color, ...newL2Color });

    // Iterate over all cellStates and update l2Image based on the cellStates
    const newL2Image = cellStates?.reduce((acc: { [key: string]: string }, row) => {
      row.forEach(cell => {
        if (cell.label !== '') {
          acc[cell.label] = cell.image;
        }
      });
      return acc;
    }, {});
    setL2Image({ ...l2Image, ...newL2Image });
  }, [doUpdateGlobalStatesToggle, initialized]);




  // Function to update a specified cell from cellStates using a flat index:
  function updateCell(index: number, newProps: Partial<GridCellProps>) {
    setCellStates(prevCellStates => {
      if (prevCellStates === undefined) return;
      const newCellStates = [...prevCellStates];
      const i = Math.floor(index / numOfCols);
      const j = index % numOfCols;
      newCellStates[i][j] = { ...newCellStates[i][j], ...newProps };
      return newCellStates;
    });
  }

  function getCellLabel(flatIndex: number) {
    const i = Math.floor(flatIndex / numOfCols);
    const j = flatIndex % numOfCols;
    return cellStates?.[i]?.[j]?.label ?? '';
  }

  function handleCustomClickEvent(flatIndex: number, eventType: 'single' | 'double' | 'long' | 'context') {
    // Convert the flat index to a 2D index
    const i = Math.floor(flatIndex / numOfCols);
    const j = flatIndex % numOfCols;
    const cellState = cellStates?.[i]?.[j];
    const isSelected = cellState?.isSelected ?? false;

    if (eventType === 'single' && selectedIndices.length === 0) {
      updateCell(flatIndex, { isEditing: true, isSelected: false });
    } else if (eventType === 'single' || eventType === 'double' || eventType === 'long' || eventType === 'context') {
      updateCell(flatIndex, { isSelected: !isSelected, isEditing: false });
      updateAllCells({ isEditing: false });
    }

  }

  function updateAllCells(newProps: Partial<GridCellProps>) {
    setCellStates(prevCellStates => {
      if (prevCellStates === undefined) return;
      return prevCellStates.map(row => row.map(cell => ({ ...cell, ...newProps })));
    });
  }

  const isThereAnyEditing = useMemo(() => {
    return cellStates?.some(row =>
      Array.isArray(row) && row.some(cell => cell.isEditing)
    );
  }, [cellStates]);

  

  const handleUnselectAll = useCallback(() => {
    updateAllCells({ isSelected: false });
  }, []);

  const handleSelectAll = useCallback(() => {
    updateAllCells({ isSelected: true });
  }, []);



  const handleUndoEditingClick = useCallback(() => {
    updateAllCells({ isEditing: false });
  }, []);

  const handleEditCellClick = useCallback(() => {
    setIRLabel(getCellLabel(selectedIndices[0]));
    setIsIROpen(true);
  }, [selectedIndices]);


  const handleClickOnCopyButton = () => {
    if (selectedIndices.length === 1) {
      setIsCopyDialogOpen(true);
    } else {
      handleCopyCell(["all"]);
    }
  }

  const handleCopyCell = useCallback((choices: string[]) => {
    const copiedCells: { [index: number]: any } = selectedIndices.reduce((acc: { [key: number]: any }, index) => {
      const copiedCell = JSON.parse(JSON.stringify(cellStates?.flat()[index]));
      if (choices.includes("all")) {
        // Force random alias for the copied cells labels to keep labels unique
        copiedCell.label = reRandomizeAliasLabel(copiedCell.label);
        acc[index] = copiedCell;

      } else {
        if (choices.includes("color")) {
          acc[index] = { ...acc[index], color: copiedCell.color };
        }
        if (choices.includes("image")) {
          acc[index] = { ...acc[index], image: copiedCell.image };
        }
      }

      return acc;
    }, {});


    let newWhatShouldICopy = { ...whatShouldICopy };
    const selectedLabel = getCellLabel(selectedIndices[0]);
    if (choices.includes("color")) {
      const color = l2Color[selectedLabel] ?? '';
      newWhatShouldICopy = { ...newWhatShouldICopy, color: color };
    }
    if (choices.includes("image")) {
      const img = l2Image[selectedLabel] ?? '';
      newWhatShouldICopy = { ...newWhatShouldICopy, image: img };
    }
    if (choices.includes("all")) {
      newWhatShouldICopy = { all: true };
    }
    setWhatShouldICopy(newWhatShouldICopy);
    setCopiedCells(copiedCells);
    handleUnselectAll();
  }, [cellStates]);


  function handlePasteCopiedCells(
    makeConsecutive: boolean,
    replaceTargetCells: boolean,
    targetFlatIndex: number
  ): void {

    setCellStates((prevCellStates: GridCellProps[][] | undefined) => {
      if (!prevCellStates) return [];

      const numOfCols = prevCellStates[0]?.length || 0; // Assuming a consistent number of columns in the grid
      const flatCellStates: GridCellProps[] = prevCellStates.flat();

      const copiedCellsIndices: number[] = Object.keys(copiedCells).map(Number).sort((a, b) => a - b);
      let cellsToPaste: GridCellProps[] = copiedCellsIndices.map(index => ({ ...copiedCells[index], index: 0 })); // Temp reset index
      if (makeConsecutive) {
        if (replaceTargetCells) {
          cellsToPaste.forEach((cell, i) => {
            const newIndex = targetFlatIndex + i;
            flatCellStates[newIndex] = { ...flatCellStates[newIndex], ...cell, index: newIndex };
          });
        } else {
          // Shifting cells to the right and updating their indices
          for (let i = flatCellStates.length - 1; i >= targetFlatIndex; i--) {
            flatCellStates[i + cellsToPaste.length] = { ...flatCellStates[i], index: i + cellsToPaste.length };
          }
          cellsToPaste.forEach((cell, i) => {
            const newIndex = targetFlatIndex + i;

            flatCellStates[newIndex] = { ...defaultCellProps, ...cell, index: newIndex };
          });
          // Ensure the grid size remains constant, trimming excess cells
          flatCellStates.length = prevCellStates.length * numOfCols;
        }
      } else {
        // Keeping relative positions (makeConsecutive = false, replaceTargetCells = true)
        const targetRow = Math.floor(targetFlatIndex / numOfCols);
        const targetCol = targetFlatIndex % numOfCols;
        const minCopiedIndexRow = Math.floor(Math.min(...copiedCellsIndices) / numOfCols);
        const minCopiedIndexCol = Math.min(...copiedCellsIndices) % numOfCols;

        copiedCellsIndices.forEach((copiedIndex, arrayIndex) => {
          const copiedRow = Math.floor(copiedIndex / numOfCols) - minCopiedIndexRow + targetRow;
          const copiedCol = copiedIndex % numOfCols - minCopiedIndexCol + targetCol;
          const newIndex = copiedRow * numOfCols + copiedCol;

          if (copiedRow < prevCellStates.length && copiedCol < numOfCols) { // Ensure within bounds
            flatCellStates[newIndex] = { ...flatCellStates[newIndex], ...cellsToPaste[arrayIndex], index: newIndex };
          }
        });
      }

      // Convert back to 2D array
      const newCellStates: GridCellProps[][] = [];
      for (let i = 0; i < prevCellStates.length; i++) {
        newCellStates.push(flatCellStates.slice(i * numOfCols, (i + 1) * numOfCols));
      }

      // Reindex the cells
      return reindexCells(newCellStates, numOfCols);


    });


    handleUnselectAll();
  };

  // Function to handle cell actions based on a specified action type
  function handleRemoveCell(action: 'MakeCellEmpty' | 'RemoveEntireRows') {
    setCellStates((prevCellStates) => {
      // Return an empty array if the previous state is not available
      if (!prevCellStates) return [];

      switch (action) {
        case 'MakeCellEmpty':
          prevCellStates = makeSelectedCellsEmpty(prevCellStates);
          break;
        case 'RemoveEntireRows':
          prevCellStates = removeSelectedCellsEntireRows(prevCellStates);
          break;
        default:
          throw new Error("Invalid action type");
      }

      return reindexCells(prevCellStates, numOfCols);
    });

    handleUnselectAll(); // Reset selection state after performing the action
  }




  function handleOnCloseIRModal(label: string | null, img: string | null): void {
    // Update label and image in the cellStates
    if (label) {
      updateCell(selectedIndices[0], { label: label });
      if (img) {
        setL2Image({ ...l2Image, [label]: img });
      }
    }


    setIsIROpen(false);
    setIRLabel(null);
    setIRLabelIndex(null);
  }

  const gridModeButton = (
    <ButtonTemplate
      isBlack={isGridMode === true}
      icon={grid}
      text={_dict.gridMode[lang]}
      onClick={() => {
        setIsGridMode(!isGridMode);
      }}
    />);

  const previousPathButton = (getParentNode() &&
    <ButtonTemplate
      isBlack={false}
      icon={arrowBack}
      text={_dict.back[lang]}
      onClick={() => {
        history.push(getURLTo().talkFromParentNodePath());
      }}
    />);



  function handleConfirmCopyDialog(choices: string[]): void {
    setIsCopyDialogOpen(false);
    handleCopyCell(choices);
  }

  return (
    <div ref={mainDivRef}>
      <div className='buttons-container'>

        {previousPathButton}
        {gridModeButton}

        {isThereAnyEditing && (
          <>
            
            <ButtonTemplate
              key={"b2"}
              onClick={handleUndoEditingClick} text={_dict.undo[lang]}
              icon={closeOutline}
            />
          </>
        )}
        {selectedIndices.length > 0 && (
          <ButtonTemplate key={"b3"} onClick={handleSelectAll} text={_dict.selectAll[lang]}
            icon={albumsOutline}
          />
        )}
        {selectedIndices.length > 0 && (
          <ButtonTemplate
            key={"b4"}
            icon={closeCircle}
            onClick={handleUnselectAll} text={_dict.unselectAll[lang]} />
        )}
        {/*selectedIndices.length === 1 && //check that selected cell is not empty
          getCellLabel(selectedIndices[0]) !== '' && (
            <ButtonTemplate
              key={"b5"}
              icon={createOutline}
              onClick={handleEditCellClick} text={_dict.edit[lang]} />
          )*/}
        {selectedIndices.length > 0 && (
          <ButtonTemplate
            key={"b6"}
            icon={trashOutline}
            onClick={() => setShowRemoveOptions(true)} text={_dict.removeCell[lang]} />
        )}
        {selectedIndices.length > 0 && (
          <ButtonTemplate
            key={"b7"}
            icon={copyOutline}
            onClick={handleClickOnCopyButton} text={_dict.copy[lang]} />
        )}



        {/* PASTE BUTTON color or image ? no need to chose among paste options! */}
        {!whatShouldICopy?.all && selectedIndices.length > 0 && Object.keys(copiedCells).length === 1
          ? (
            <ButtonTemplate
              key={"b9"}
              icon={clipboardOutline}
              onClick={() => {
                selectedIndices.map(async (index) => await handlePasteCopiedCells(false, true, index));
                //handlePasteCopiedCells(false, true, selectedIndices[0])}
              } } text={_dict.justPaste[lang]} />
          ) :
          selectedIndices.length === 1 && Object.keys(copiedCells).length > 0 && (

            <ButtonTemplate
              key={"b8"}
              icon={clipboardOutline}
              onClick={() => setShowPasteOptions(true)} text={_dict.pasteHere[lang]} />
          )
        }




      </div>
      {isParentMode && <div >
        {selectedIndices.length === 0 && !isThereAnyEditing && _dict.clickToEditCellDoubleClickOrLongPressToSelect[lang]}
      </div>}
      <div style={{ marginTop: '10px' }}>
        {cellStates?.map((row, rowIndex) => (
          <div key={rowIndex} style={{ display: 'flex', flexWrap: 'wrap' }}>
            {row.map((cellState, colIndex) => {
              return isParentMode ? (
                <Cell
                  {...cellState} // Spread operator to pass all cellState properties
                  key={cellState.index + cellState.label}
                  updateCell={updateCell}
                  handleCustomClickEvent={handleCustomClickEvent}

                />
              ) : (
                <Card
                  key={cellState.index}
                  index={cellState.index}
                  label={cellState.label || ""}
                />
              );
            })}
          </div>
        ))}
      </div>
      <IonActionSheet
        isOpen={showPasteOptions}
        onDidDismiss={() => setShowPasteOptions(false)}
        buttons={[
          {
            text: _dict.pasteHereRelative[lang],
            handler: () => handlePasteCopiedCells(false, true, selectedIndices[0]),
          },
          {
            text: _dict.pasteHereReplace[lang],
            handler: () => handlePasteCopiedCells(true, true, selectedIndices[0]),
          },
          {
            text: _dict.pasteHereShift[lang],
            handler: () => handlePasteCopiedCells(true, false, selectedIndices[0]),
          }


        ]} />
      <IonActionSheet
        isOpen={showRemoveOptions}
        onDidDismiss={() => setShowRemoveOptions(false)}
        buttons={[
          {
            text: _dict.emptyCell[lang],
            handler: () => handleRemoveCell('MakeCellEmpty'),
          },
          {
            text: _dict.removeEntireRows[lang],
            handler: () => handleRemoveCell('RemoveEntireRows'),
          }
        ]} />
      {IRLabel !== null && isIROpen === true && <ImageRetrievalModal
        onClose={handleOnCloseIRModal}
        label={IRLabel ?? ''}
        isOpen={isIROpen}
        initialImage={IRLabel ? getImage(IRLabel) || null : null}
      />}
      {isCopyDialogOpen && <CopyDialog isOpen={isCopyDialogOpen}
        onClose={() => { setIsCopyDialogOpen(false) }}
        onConfirm={(choices: string[]) => handleConfirmCopyDialog(choices)}


      />}
    </div>
  );
};

export default GridMainComponent;


/*
  useEffect(() => {
    if(nodesUpdatedToggle === undefined) return;
    // Iterate over all cellStates and gather all the "label" and "image" property
    // in a single dictionary:
    const upL2Image = Object.keys(cellStates).reduce((acc: { [key: string]: string }, key) => {
      const cellState = cellStates[parseInt(key)];
      if (cellState?.label !== undefined) {
        acc[cellState.label] = l2Image[cellState.label] || '';
      }
      return acc;
    }, {});

    setL2Image({ ...l2Image, ...upL2Image }); 
  }, [nodesUpdatedToggle]);

  const isThereAnyEditing = useMemo(() => {
    return Object.values(cellStates).some(state => state.isEditing);
  }, [cellStates]);

  
  const selectedIndices = Object.keys(cellStates).filter((key) => cellStates[parseInt(key)]?.isSelected).map((key) => parseInt(key));

  const indexToLabelDict = useMemo(() => {
    return Object.keys(cellStates).reduce((acc: { [key: number]: string }, key) => {
      const index = parseInt(key);
      const cellState = cellStates[index];
      if (cellState?.label !== undefined) {
        acc[index] = cellState.label;
      }
      return acc;
    }, {});
  }, [cellStates]);

  const updateCellStateByIndex = useCallback(
    (index: number, { stateName, targetValue, defaultValue }: { stateName: string; targetValue: any; defaultValue?: any }) => {
      setCellStates((prevCellStates) => {
        const updatedStates = { ...prevCellStates };
        updatedStates[index] = {
          ...updatedStates[index],
          [stateName]: targetValue,
        };

        if (defaultValue !== undefined) {
          Object.keys(updatedStates).forEach((key) => {
            if (key !== index.toString()) {
              updatedStates[parseInt(key)] = {
                ...updatedStates[parseInt(key)],
                [stateName]: defaultValue,
              };
            }
          });
        }

        return updatedStates;
      });
      setCellStatesUpdateToggle(prevCellStatesUpdateToggle => !prevCellStatesUpdateToggle);
    },
    [cellStates]
  );



  

  const handleToggleAllCells = useCallback((stateName: string, targetValue: any) => {
    setCellStates((prevCellStates) => {
      const updatedStates = { ...prevCellStates };
      Object.keys(updatedStates).forEach((key) => {
        updatedStates[parseInt(key)] = {
          ...updatedStates[parseInt(key)],
          [stateName]: targetValue,
        };
      });
      return updatedStates;
    });
  }, []);

  const handleCustomClickEvent = useCallback((index: number, eventType: 'single' | 'double' | 'long' | 'context') => {
    const cellState = cellStates[index];
    const isSelected = cellState?.isSelected ?? false;
    console.log("Clicked on cell", index, "with event type", eventType, "and isSelected", isSelected)
    if (eventType === 'single' && selectedIndices.length === 0) {
      updateCellStateByIndex(index, { stateName: 'isEditing', targetValue: true, defaultValue: false });
    } else if (eventType === 'single' || eventType === 'double' || eventType === 'long' || eventType === 'context') {
      updateCellStateByIndex(index, { stateName: 'isSelected', targetValue: !isSelected });
      updateCellStateByIndex(index, { stateName: 'isEditing', targetValue: false, defaultValue: false });
    }
  }, [cellStates, selectedIndices, updateCellStateByIndex]);



  const getFirstEditingIndex = useCallback(() => {
    const res = Object.keys(cellStates).find((key) => cellStates[parseInt(key)]?.isEditing) || '';
    if (res === '') return -1;
    return parseInt(res);
  }, [cellStates]);

  const handleUnselectAll = useCallback(() => {
    handleToggleAllCells('isSelected', false);
  }, [handleToggleAllCells]);

  const handleSelectAll = useCallback(() => {
    handleToggleAllCells('isSelected', true);
  }, [handleToggleAllCells]);

  const handleCheckmarkClick = useCallback(() => {

    const editingIndex = getFirstEditingIndex();
    if (editingIndex === -1) return;
    

    
    //if (cellStates[editingIndex]?.label === '' || cellStates[editingIndex]?.label === undefined) {
    //  handleToggleAllCells('isEditing', false);
    //} else {
      updateCellStateByIndex(editingIndex, { stateName: 'mustConfirm', targetValue: true });
    //}
    handleUnselectAll();
  }, [cellStates, getFirstEditingIndex, handleToggleAllCells, handleUnselectAll, updateCellStateByIndex]);

  const handleUndoEditingClick = useCallback(() => {
    handleToggleAllCells('isEditing', false);
  }, [handleToggleAllCells]);

  const getCellContentByIndex = useCallback((index: number) => {
    return cellStates[index]?.label ?? '';
  }, [cellStates]);

  const handleEditCellClick = useCallback(() => {
    setIsIROpen(true);
  }, [selectedIndices, cellStates]);


  const handleRemoveClick = useCallback(() => {
    setCellStates((prevCellStates) => {
      let updatedStates = { ...prevCellStates };
      const sortedIndices = selectedIndices.sort((a, b) => b - a);
      sortedIndices.forEach((index) => {
        for (let i = index; i < totalCells - 1; i++) {
          updatedStates[i] = { ...updatedStates[i + 1] };
        }
        delete updatedStates[totalCells - 1];
      });
      return updatedStates;
    });
    handleUnselectAll();
  }, [handleUnselectAll, totalCells, cellStates, selectedIndices]);

  const handleMakeCellEmpty = useCallback(() => {
    setCellStates((prevCellStates) => {
      let updatedStates = { ...prevCellStates };
      selectedIndices.forEach((index) => {
        updatedStates[index] = { ...updatedStates[index], label: undefined, isCard: false };
      });
      return updatedStates;
    });
    handleUnselectAll();
  }, [handleUnselectAll, cellStates, selectedIndices]);

  
  

  const handleCopyCell = useCallback(() => {
    const copiedCells: { [index: number]: any } = selectedIndices.reduce((acc: { [key: number]: any }, index) => {
      acc[index] = JSON.parse(JSON.stringify(cellStates[index]));
      return acc;
    }, {});
    setCopiedCells(copiedCells);
    handleUnselectAll();
  }, [cellStates]);

  const handlePasteCell = useCallback((mode:string) => {
    const selectedIndex = selectedIndices[0];
    const updatedCellStates = { ...cellStates };

    if(mode==="shift"){
    // Take all indices in updatedCellStates which are equal or greater than selectedIndex
    //and shift them to the right by the number of copiedCells.
    //If last cell is not undefined and not empty, create a new cell at the end. 
      const copiedCellsLength = Object.keys(copiedCells).length;
      for (let i = totalCells - 1; i >= selectedIndex; i--) {
        updatedCellStates[i + copiedCellsLength] = {...updatedCellStates[i]};
        delete updatedCellStates[i];
      }
    }
    // Paste the copied cells consecutively starting from selected index in updatedCellStates
    Object.keys(copiedCells).forEach((key, index) => {
      updatedCellStates[selectedIndex + index] = JSON.parse(JSON.stringify(copiedCells[parseInt(key)]));
    });
    
    
    setCellStates(updatedCellStates);
    handleUnselectAll();
}, [cellStates, copiedCells, totalCells]);


  



  return (
      <div>
        <div className='buttons-container'>
          {isParentMode ? <ButtonTemplate onClick={() => { setIsGridMode(false); } } icon={backspaceOutline} text={_dict.deactivateGridMode[lang]} /> : null}

        
          {isThereAnyEditing && (
            <>
              <ButtonTemplate onClick={handleCheckmarkClick} icon={checkmark} 
              text={_dict.done[lang]} 
                
              />
              <ButtonTemplate
              onClick={handleUndoEditingClick} text={_dict.undo[lang]}
              icon={closeOutline}
              />
            </>
          )}
          {selectedIndices.length > 0 && (
            <ButtonTemplate onClick={handleSelectAll} text={_dict.selectAll[lang]}
            icon={albumsOutline}
            />
          )}
          {selectedIndices.length > 1 && (
            <ButtonTemplate
            icon={closeCircle}
            onClick={handleUnselectAll} text={_dict.unselectAll[lang]} />
          )}
          {selectedIndices.length === 1 && //check that selected cell is not empty
            cellStates[selectedIndices[0]]?.label !== undefined &&
           (
            <ButtonTemplate
            icon={createOutline}
            onClick={handleEditCellClick} text={_dict.edit[lang]} />
          )}
          {selectedIndices.length > 0 && (
            <ButtonTemplate
            icon={trashOutline}
            onClick={() => setShowRemoveOptions(true)} text={_dict.removeCell[lang]} />
          )}
          {selectedIndices.length > 0 && (
            <ButtonTemplate
            icon={copyOutline}
            onClick={handleCopyCell} text={_dict.copy[lang]} />
          )}
          {selectedIndices.length === 1 && Object.keys(copiedCells).length > 0 && (
            <ButtonTemplate
            icon={clipboardOutline}
            onClick={() => setShowPasteOptions(true)} text={_dict.pasteHere[lang]} />
          )}
          

      </div>
      {isParentMode &&<div style={{height:"15px"}}>
        {selectedIndices.length === 0 && !isThereAnyEditing && _dict.clickToEditCellDoubleClickOrLongPressToSelect[lang]}
      </div>}
      <div style={{ marginTop: '10px' }}>
      {cells2D.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', flexWrap: 'wrap' }}>
          {row.map((cellState, colIndex) => {
            const cellIndex = absoluteIndex++; // Use and increment the absolute index
            return isParentMode ? (
              <Cell
                key={cellIndex} 
                index={cellIndex}
                updateCellStateByIndex={updateCellStateByIndex}
                getCellContentByIndex={getCellContentByIndex}
                handleCustomClickEvent={handleCustomClickEvent}
                {...cellState} // Spread operator to pass all cellState properties
              />
            ) : (
              <Card
                key={cellIndex}
                label={cellState.label}
                sentenceIonColRef={sentenceIonColRef}
                setVideoURL={setVideoURL}
              />
            );
          })}
        </div>
      ))}

          
        </div>
        <IonActionSheet
          isOpen={showPasteOptions}
          onDidDismiss={() => setShowPasteOptions(false)}
          buttons={[
            {
              text: _dict.pasteHereReplace[lang],
              handler: () => handlePasteCell("replace"),
            },
            {
              text: _dict.pasteHereShift[lang],
              handler: () => handlePasteCell("shift"),
            }
            
          ]} />
        <IonActionSheet
          isOpen={showRemoveOptions}
          onDidDismiss={() => setShowRemoveOptions(false)}
          buttons={[
            {
              text: _dict.removeEntireCell[lang],
              handler: () => handleRemoveClick(),
            },
            {
              text: _dict.emptyCell[lang],
              handler: () => handleMakeCellEmpty(),
            }
          ]} />
          <ImageRetrievalModal
          onClose={() => {setIsIROpen(false); setIRLabel(null);}}
          label={IRLabel ?? ''}
          isOpen={isIROpen}
          initialImage={IRLabel ? l2Image[IRLabel] || null : null}
        />
      </div>
  );
};

export default GridMainComponent;*/
